<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import CustomerDashboard from '../components/CustomerDashboard.svelte';
  import ClosureDaysPanel from '../components/ClosureDaysPanel.svelte';
  import { _ } from '../lib/i18n.js';
  import { link } from 'svelte-spa-router';
  import {
    Users, FileText, Box, RefreshCw, CheckCircle, AlertCircle,
    Plus, ArrowRight, TrendingUp, Target, BarChart3, Loader, Euro
  } from 'lucide-svelte';
  import { csvApi, statsApi, timesheetsApi } from '../stores/api.js';
  import { isCustomer, isAdmin } from '../stores/roles.js';

  let syncLoading = false;
  let syncMessage = '';
  let syncSuccess = false;
  let statsLoading = true;
  let stats = null;
  let statsError = null;

  // User profitability data (admin only)
  let userProfitability = [];
  let profitabilityTotals = {
    totalHours: 0,
    billableHours: 0,
    nonBillableHours: 0,
    cost: 0,
    revenue: 0,
    profit: 0,
    margin: 0
  };
  let loadingProfitability = false;
  let profitabilityYear = new Date().getFullYear();

  onMount(() => {
    loadStats();
  });

  // Load profitability when admin and stats are loaded
  $: if ($isAdmin && !statsLoading && userProfitability.length === 0 && !loadingProfitability) {
    loadUserProfitability();
  }

  async function loadUserProfitability() {
    loadingProfitability = true;
    try {
      console.log(`[Home] Loading user profitability for year ${profitabilityYear}`);
      const response = await timesheetsApi.getUserProfitability(profitabilityYear);
      userProfitability = response.users || [];
      profitabilityTotals = response.totals || {
        totalHours: 0,
        billableHours: 0,
        nonBillableHours: 0,
        cost: 0,
        revenue: 0,
        profit: 0,
        margin: 0
      };
      console.log(`[Home] Loaded profitability for ${userProfitability.length} users`);
    } catch (err) {
      console.error('[Home] Error loading user profitability:', err);
    } finally {
      loadingProfitability = false;
    }
  }

  async function loadStats() {
    try {
      statsLoading = true;
      statsError = null;
      stats = await statsApi.get();
      console.log('[Home] Dashboard stats loaded:', stats);
    } catch (error) {
      console.error('[Home] Failed to load stats:', error);
      statsError = error.message;
    } finally {
      statsLoading = false;
    }
  }

  async function handleCsvSync() {
    try {
      syncLoading = true;
      syncMessage = '';
      syncSuccess = false;

      const result = await csvApi.sync();

      if (result.success) {
        syncSuccess = true;
        const { customers } = result.result;
        syncMessage = $_('home.syncSuccess', {
          values: {
            customersProcessed: customers.processed,
            customersCreated: customers.created,
            customersUpdated: customers.updated
          }
        });
        // Reload stats after sync
        await loadStats();
      } else {
        syncSuccess = false;
        syncMessage = $_('home.syncError');
      }
    } catch (error) {
      syncSuccess = false;
      syncMessage = $_('home.genericError', { values: { message: error.message } });
      console.error('[Home] CSV sync error:', error);
    } finally {
      syncLoading = false;
      setTimeout(() => {
        syncMessage = '';
      }, 10000);
    }
  }

  function formatNumber(num) {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('it-IT').format(num);
  }
</script>

<Layout>
  {#if $isCustomer}
    <CustomerDashboard />
  {:else}
  <div class="max-w-7xl mx-auto">
    <!-- Stats Cards -->
    <div class="grid grid-cols-3 gap-4 mb-8">
      <!-- Customers Stat -->
      <a href="/customers" use:link class="card p-4 group border-2 border-gray-700 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all duration-100">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.1);">
            <Users size={20} class="text-primary-500" />
          </div>
          <ArrowRight size={16} class="text-gray-500 group-hover:text-primary-500 transition-all" />
        </div>
        <div class="text-2xl font-bold text-heading font-display tracking-wider">
          {#if statsLoading}
            <div class="h-8 w-16 bg-gray-300 dark:bg-gray-700 animate-pulse"></div>
          {:else}
            {formatNumber(stats?.customers)}
          {/if}
        </div>
        <div class="text-sm uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('nav.customers')}</div>
      </a>

      <!-- Products Stat -->
      <a href="/products" use:link class="card p-4 group border-2 border-gray-700 dark:border-gray-700 hover:border-magenta-500 dark:hover:border-magenta-500 transition-all duration-100">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 border-2 border-magenta-500 flex items-center justify-center" style="background: rgba(255, 0, 255, 0.1);">
            <Box size={20} class="text-magenta-500" />
          </div>
          <ArrowRight size={16} class="text-gray-500 group-hover:text-magenta-500 transition-all" />
        </div>
        <div class="text-2xl font-bold text-heading font-display tracking-wider">
          {#if statsLoading}
            <div class="h-8 w-16 bg-gray-300 dark:bg-gray-700 animate-pulse"></div>
          {:else}
            {formatNumber(stats?.products)}
          {/if}
        </div>
        <div class="text-sm uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('nav.products')}</div>
      </a>

      <!-- Proposals Stat -->
      <a href="/proposals" use:link class="card p-4 group border-2 border-gray-700 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all duration-100">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.05);">
            <FileText size={20} class="text-primary-500" />
          </div>
          <ArrowRight size={16} class="text-gray-500 group-hover:text-primary-500 transition-all" />
        </div>
        <div class="text-2xl font-bold text-heading font-display tracking-wider">
          {#if statsLoading}
            <div class="h-8 w-16 bg-gray-300 dark:bg-gray-700 animate-pulse"></div>
          {:else}
            {formatNumber(stats?.proposals)}
          {/if}
        </div>
        <div class="text-sm uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('nav.proposals')}</div>
      </a>
    </div>

    <!-- Sales Targets Widget -->
    {#if stats?.salesTargetSummary}
      <a href="/reports" use:link class="card p-6 group mb-8 block border-2 border-gray-700 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all duration-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.1);">
              <Target size={24} class="text-primary-500" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-heading font-display tracking-wider">{$_('home.salesTargets.title')} {stats.salesTargetSummary.year}</h3>
              <p class="text-sm" style="color: var(--color-text-muted);">{stats.salesTargetSummary.productsWithTarget} {$_('home.salesTargets.productsWithTarget')}</p>
            </div>
          </div>
          <div class="flex items-center space-x-6">
            <div class="text-right">
              <div class="text-3xl font-bold text-primary-500 font-display crt-glow">{stats.salesTargetSummary.progressPercentage}%</div>
              <div class="text-sm" style="color: var(--color-text-muted);">{stats.salesTargetSummary.totalSoldQuantity.toLocaleString()} / {stats.salesTargetSummary.totalTargetQuantity.toLocaleString()}</div>
            </div>
            <div class="w-20 h-20 relative">
              <svg class="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" stroke-width="3" class="text-gray-700" />
                <circle cx="18" cy="18" r="16" fill="none" stroke="url(#progressGradient)" stroke-width="3" stroke-linecap="square"
                  stroke-dasharray="{Math.min(stats.salesTargetSummary.progressPercentage, 100) * 1.005} 100.5"
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stop-color="#00f0ff" />
                    <stop offset="100%" stop-color="#ff00ff" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <ArrowRight size={20} class="text-gray-500 group-hover:text-primary-500 transition-all" />
          </div>
        </div>
      </a>
    {/if}

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Shortcuts Section -->
      <div class="card p-6 border-2 border-gray-700 dark:border-gray-700">
        <div class="flex items-center space-x-3 mb-6">
          <div class="w-10 h-10 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.1);">
            <TrendingUp class="w-5 h-5 text-primary-500" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-heading font-display tracking-wider">{$_('home.shortcuts.title')}</h2>
            <p style="color: var(--color-text-muted);">{$_('home.shortcuts.subtitle')}</p>
          </div>
        </div>

        <div class="space-y-3">
          <!-- CSV Sync Button -->
          <button
            on:click={handleCsvSync}
            disabled={syncLoading}
            class="w-full flex items-center space-x-3 p-4 border-2 border-primary-500/30 hover:border-primary-500 transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed group"
            style="background: rgba(0, 240, 255, 0.05);"
          >
            <div class="w-10 h-10 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.1);">
              {#if syncLoading}
                <RefreshCw size={20} class="text-primary-500 animate-spin" />
              {:else}
                <RefreshCw size={20} class="text-primary-500" />
              {/if}
            </div>
            <div class="flex-1 text-left">
              <div class="font-bold text-heading uppercase tracking-wide text-sm">
                {#if syncLoading}
                  {$_('home.syncing')}
                {:else}
                  {$_('home.syncCsv')}
                {/if}
              </div>
              <div class="text-sm" style="color: var(--color-text-muted);">{$_('home.shortcuts.syncDescription')}</div>
            </div>
          </button>

          <!-- Sync Status Message -->
          {#if syncMessage}
            <div class="p-3 text-sm border-2 {syncSuccess ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400'}" style="background: {syncSuccess ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};">
              <div class="flex items-start space-x-2">
                {#if syncSuccess}
                  <CheckCircle size={16} class="text-green-500 flex-shrink-0 mt-0.5" />
                {:else}
                  <AlertCircle size={16} class="text-red-500 flex-shrink-0 mt-0.5" />
                {/if}
                <span class="leading-relaxed">{syncMessage}</span>
              </div>
            </div>
          {/if}

          <!-- Quick Links -->
          <div class="grid grid-cols-2 gap-3 pt-2">
            <a
              href="/customers"
              use:link
              class="flex items-center space-x-2 p-3 border-2 border-gray-700 hover:border-primary-500 hover:text-primary-500 transition-all duration-100 group"
              style="background: var(--color-surface-light);"
            >
              <Plus size={18} class="text-gray-500 group-hover:text-primary-500" />
              <span class="text-sm font-bold uppercase tracking-wide group-hover:text-primary-500" style="color: var(--color-text-body);">{$_('home.shortcuts.newCustomer')}</span>
            </a>
            <a
              href="/proposals"
              use:link
              class="flex items-center space-x-2 p-3 border-2 border-gray-700 hover:border-magenta-500 hover:text-magenta-500 transition-all duration-100 group"
              style="background: var(--color-surface-light);"
            >
              <Plus size={18} class="text-gray-500 group-hover:text-magenta-500" />
              <span class="text-sm font-bold uppercase tracking-wide group-hover:text-magenta-500" style="color: var(--color-text-body);">{$_('home.shortcuts.newProposal')}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Proposals Status Section -->
      <div class="card p-6 border-2 border-gray-700 dark:border-gray-700">
        <div class="flex items-center space-x-3 mb-6">
          <div class="w-10 h-10 border-2 border-magenta-500 flex items-center justify-center" style="background: rgba(255, 0, 255, 0.1);">
            <FileText class="w-5 h-5 text-magenta-500" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-heading font-display tracking-wider">{$_('home.proposalsOverview.title')}</h2>
            <p style="color: var(--color-text-muted);">{$_('home.proposalsOverview.subtitle')}</p>
          </div>
        </div>

        {#if statsLoading}
          <div class="space-y-3">
            {#each [1, 2, 3, 4] as _}
              <div class="h-12 animate-pulse" style="background: var(--color-surface-light);"></div>
            {/each}
          </div>
        {:else if statsError}
          <div class="text-center py-8" style="color: var(--color-text-muted);">
            <AlertCircle size={32} class="mx-auto mb-2 text-gray-500" />
            <p>{$_('home.proposalsOverview.loadError')}</p>
          </div>
        {:else}
          <div class="space-y-2">
            <!-- Draft Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-gray-500" style="background: var(--color-surface-light);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm" style="color: var(--color-text-body);">{$_('proposals.status.draft')}</span>
              </div>
              <span class="text-lg font-bold text-heading font-display">{formatNumber(stats?.proposalsByStatus?.draft || 0)}</span>
            </div>

            <!-- Submitted Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-yellow-500" style="background: rgba(234, 179, 8, 0.05);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm text-yellow-400">{$_('proposals.status.submitted')}</span>
              </div>
              <span class="text-lg font-bold text-yellow-400 font-display">{formatNumber(stats?.proposalsByStatus?.submitted || 0)}</span>
            </div>

            <!-- Approved Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-green-500" style="background: rgba(34, 197, 94, 0.05);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm text-green-400">{$_('proposals.status.approved')}</span>
              </div>
              <span class="text-lg font-bold text-green-400 font-display">{formatNumber(stats?.proposalsByStatus?.approved || 0)}</span>
            </div>

            <!-- Sent Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-blue-500" style="background: rgba(59, 130, 246, 0.05);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm text-blue-400">{$_('proposals.status.sent')}</span>
              </div>
              <span class="text-lg font-bold text-blue-400 font-display">{formatNumber(stats?.proposalsByStatus?.sent || 0)}</span>
            </div>

            <!-- Confirmed Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-indigo-500" style="background: rgba(99, 102, 241, 0.05);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm text-indigo-400">{$_('proposals.status.confirmed')}</span>
              </div>
              <span class="text-lg font-bold text-indigo-400 font-display">{formatNumber(stats?.proposalsByStatus?.confirmed || 0)}</span>
            </div>

            <!-- Completed Proposals -->
            <div class="flex items-center justify-between p-3 border-l-4 border-emerald-500" style="background: rgba(16, 185, 129, 0.05);">
              <div class="flex items-center space-x-3">
                <span class="font-bold uppercase tracking-wide text-sm text-emerald-400">{$_('proposals.status.completed')}</span>
              </div>
              <span class="text-lg font-bold text-emerald-400 font-display">{formatNumber(stats?.proposalsByStatus?.completed || 0)}</span>
            </div>
          </div>

          <div class="mt-4 pt-4 border-t-2" style="border-color: var(--color-border-default);">
            <a
              href="/proposals"
              use:link
              class="flex items-center justify-center space-x-2 text-primary-500 hover:text-primary-400 font-bold uppercase tracking-wider text-sm"
            >
              <span>{$_('home.proposalsOverview.viewAll')}</span>
              <ArrowRight size={16} />
            </a>
          </div>
        {/if}
      </div>
    </div>

    <!-- Upcoming company closures. Read-only here too: the calendar is edited
         in Settings, which is where the admin-only write endpoints live. -->
    <div class="mt-8">
      <ClosureDaysPanel />
    </div>

    <!-- User Profitability Section (Admin Only) -->
    {#if $isAdmin}
      <div class="card overflow-hidden mt-8 border-2 border-gray-700 dark:border-gray-700">
        <div class="p-4 border-b-2 flex justify-between items-center" style="border-color: var(--color-border-default);">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 border-2 border-green-500 flex items-center justify-center" style="background: rgba(34, 197, 94, 0.1);">
              <Euro size={20} class="text-green-500" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-heading font-display tracking-wider">{$_('reports.userProfitability.title')}</h3>
              <p class="text-sm" style="color: var(--color-text-muted);">{profitabilityYear}</p>
            </div>
          </div>
        </div>

        {#if loadingProfitability}
          <div class="p-8 text-center">
            <Loader size={32} class="mx-auto text-primary-500 animate-spin mb-4" />
            <p style="color: var(--color-text-muted);">{$_('common.loading')}</p>
          </div>
        {:else}
          <!-- Profitability Summary Cards -->
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4 p-4" style="background: var(--color-surface-light);">
            <div class="p-4 border-2 border-gray-700" style="background: var(--color-surface-dark);">
              <div class="text-xs uppercase tracking-wider mb-1" style="color: var(--color-text-muted);">{$_('reports.userProfitability.totalRevenue')}</div>
              <div class="text-xl font-bold text-green-400 font-display">€ {profitabilityTotals.revenue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="p-4 border-2 border-gray-700" style="background: var(--color-surface-dark);">
              <div class="text-xs uppercase tracking-wider mb-1" style="color: var(--color-text-muted);">{$_('reports.userProfitability.totalCost')}</div>
              <div class="text-xl font-bold text-red-400 font-display">€ {profitabilityTotals.cost.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="p-4 border-2 border-gray-700" style="background: var(--color-surface-dark);">
              <div class="text-xs uppercase tracking-wider mb-1" style="color: var(--color-text-muted);">{$_('reports.userProfitability.totalProfit')}</div>
              <div class="text-xl font-bold font-display {profitabilityTotals.profit >= 0 ? 'text-green-400' : 'text-red-400'}">€ {profitabilityTotals.profit.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="p-4 border-2 border-gray-700" style="background: var(--color-surface-dark);">
              <div class="text-xs uppercase tracking-wider mb-1" style="color: var(--color-text-muted);">{$_('reports.userProfitability.totalHours')}</div>
              <div class="text-xl font-bold text-heading font-display">{profitabilityTotals.totalHours.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</div>
            </div>
            <div class="p-4 border-2 border-gray-700" style="background: var(--color-surface-dark);">
              <div class="text-xs uppercase tracking-wider mb-1" style="color: var(--color-text-muted);">{$_('reports.userProfitability.hoursProgress')}</div>
              {#if profitabilityTotals.expectedHoursYear}
                <div class="text-xl font-bold font-display {profitabilityTotals.hoursProgress >= 100 ? 'text-green-400' : profitabilityTotals.hoursProgress >= 80 ? 'text-yellow-400' : 'text-heading'}">{profitabilityTotals.hoursProgress}%</div>
                <div class="text-xs mt-1" style="color: var(--color-text-muted);">{profitabilityTotals.totalHours.toFixed(0)}h / {profitabilityTotals.expectedHoursYear.toFixed(0)}h</div>
              {:else}
                <div class="text-xl font-bold text-gray-500 font-display">-</div>
              {/if}
            </div>
          </div>

          <!-- User Profitability Table -->
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead style="background: var(--color-surface-light);">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.user')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.hours')}</th>
                  <th class="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.hoursTarget')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.hourlyCost')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.cost')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.revenue')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.profit')}</th>
                  <th class="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">{$_('reports.userProfitability.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {#each userProfitability as user (user.userId)}
                  <tr class="border-b transition-colors duration-100 hover:bg-gray-100 dark:hover:bg-gray-800/50" style="border-color: var(--color-border-default);">
                    <td class="px-4 py-4">
                      <div class="font-bold text-heading">{user.email || user.userId}</div>
                      <div class="text-xs" style="color: var(--color-text-muted);">
                        {user.billableHours.toFixed(1)}h {$_('reports.userProfitability.billable')} / {user.nonBillableHours.toFixed(1)}h {$_('reports.userProfitability.nonBillable')}
                      </div>
                    </td>
                    <td class="px-4 py-4 text-right text-sm text-heading">
                      {user.totalHours.toFixed(1)}h
                    </td>
                    <td class="px-4 py-4 text-center">
                      {#if user.expectedHoursYear}
                        <div class="flex flex-col items-center">
                          <span class="inline-flex items-center px-2 py-1 text-xs font-bold border {user.hoursProgress >= 100 ? 'border-green-500 text-green-400' : user.hoursProgress >= 80 ? 'border-yellow-500 text-yellow-400' : 'border-gray-600 text-gray-300'}" style="background: {user.hoursProgress >= 100 ? 'rgba(34, 197, 94, 0.1)' : user.hoursProgress >= 80 ? 'rgba(234, 179, 8, 0.1)' : 'rgba(100, 116, 139, 0.1)'};">
                            {user.hoursProgress}%
                          </span>
                          <span class="text-xs mt-1" style="color: var(--color-text-muted);">{user.totalHours.toFixed(0)} / {user.expectedHoursYear.toFixed(0)}h</span>
                        </div>
                      {:else}
                        <span class="text-gray-500">-</span>
                      {/if}
                    </td>
                    <td class="px-4 py-4 text-right text-sm" style="color: var(--color-text-body);">
                      {#if user.hourlyCost > 0}
                        € {user.hourlyCost.toFixed(2)}
                      {:else}
                        <span class="text-gray-500">-</span>
                      {/if}
                    </td>
                    <td class="px-4 py-4 text-right text-sm font-bold text-red-400">
                      € {user.cost.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td class="px-4 py-4 text-right text-sm font-bold text-green-400">
                      € {user.revenue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td class="px-4 py-4 text-right text-sm font-bold {user.profit >= 0 ? 'text-green-400' : 'text-red-400'}">
                      € {user.profit.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td class="px-4 py-4 text-right">
                      <span class="inline-flex items-center px-2 py-1 text-xs font-bold border {user.margin >= 50 ? 'border-green-500 text-green-400' : user.margin >= 0 ? 'border-yellow-500 text-yellow-400' : 'border-red-500 text-red-400'}" style="background: {user.margin >= 50 ? 'rgba(34, 197, 94, 0.1)' : user.margin >= 0 ? 'rgba(234, 179, 8, 0.1)' : 'rgba(239, 68, 68, 0.1)'};">
                        {user.margin}%
                      </span>
                    </td>
                  </tr>
                {:else}
                  <tr>
                    <td colspan="8" class="px-4 py-12 text-center" style="color: var(--color-text-muted);">
                      {$_('reports.userProfitability.noData')}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {/if}
  </div>
  {/if}
</Layout>
